import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Row {
  id: string;
  product_id: string;
  question: string;
  answer: string | null;
  status: "pending" | "answered" | "hidden";
  created_at: string;
  product_name?: string;
}

interface Props {
  lang: "es" | "en";
  toast: (m: string) => void;
}

export function QAModeration({ lang, toast }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"pending" | "answered" | "hidden">("pending");
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("product_questions")
      .select("id, product_id, question, answer, status, created_at, products(name)")
      .eq("status", tab)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) toast(`⚠️ ${error.message}`);
    const mapped: Row[] = (data || []).map((r: any) => ({
      id: r.id,
      product_id: r.product_id,
      question: r.question,
      answer: r.answer,
      status: r.status,
      created_at: r.created_at,
      product_name: r.products?.name,
    }));
    setRows(mapped);
    setLoading(false);
  }, [tab, toast]);

  useEffect(() => { refresh(); }, [refresh]);

  const answer = async (row: Row) => {
    const text = (drafts[row.id] ?? row.answer ?? "").trim();
    if (text.length < 2) {
      toast(lang === "es" ? "Respuesta muy corta" : "Answer too short");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("product_questions")
      .update({
        answer: text,
        answered_by: user?.id ?? null,
        answered_at: new Date().toISOString(),
        status: "answered",
      })
      .eq("id", row.id);
    if (error) { toast(`⚠️ ${error.message}`); return; }
    toast(lang === "es" ? "✓ Respuesta publicada" : "✓ Answer posted");
    refresh();
  };

  const setStatus = async (id: string, status: "pending" | "answered" | "hidden") => {
    const { error } = await supabase
      .from("product_questions")
      .update({ status })
      .eq("id", id);
    if (error) { toast(`⚠️ ${error.message}`); return; }
    refresh();
  };

  const L = (es: string, en: string) => (lang === "es" ? es : en);

  return (
    <div>
      <div className="fls" style={{ marginBottom: 12 }}>
        {(["pending", "answered", "hidden"] as const).map((s) => (
          <button
            key={s}
            className={`fc ${tab === s ? "on" : ""}`}
            onClick={() => setTab(s)}
          >
            {s === "pending" ? L("Pendientes", "Pending")
              : s === "answered" ? L("Respondidas", "Answered")
              : L("Ocultas", "Hidden")}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: "var(--tx3)", fontSize: 12 }}>{L("Cargando…", "Loading…")}</p>}
      {!loading && rows.length === 0 && (
        <p style={{ color: "var(--tx3)", fontSize: 12 }}>
          {L("No hay preguntas en esta vista.", "No questions in this view.")}
        </p>
      )}

      {rows.map((r) => (
        <div key={r.id} className="fcard" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 4 }}>
            {r.product_name || r.product_id} · {new Date(r.created_at).toLocaleDateString()}
          </div>
          <p style={{ fontSize: 13, fontWeight: 600, margin: "4px 0" }}>
            <span style={{ color: "var(--ac3)", marginRight: 4 }}>Q.</span>{r.question}
          </p>
          <textarea
            value={drafts[r.id] ?? r.answer ?? ""}
            onChange={(e) => setDrafts((p) => ({ ...p, [r.id]: e.target.value }))}
            placeholder={L("Escribe la respuesta del equipo…", "Write the team reply…")}
            rows={3}
            style={{
              width: "100%", padding: "8px 10px", fontSize: 12,
              border: "1.5px solid var(--bd)", borderRadius: "var(--r1)",
              fontFamily: "var(--f)", marginTop: 6, resize: "vertical",
            }}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <button className="btn bp bs" onClick={() => answer(r)}>
              {r.status === "answered"
                ? L("Actualizar respuesta", "Update answer")
                : L("Responder y publicar", "Answer & publish")}
            </button>
            {r.status !== "hidden" && (
              <button className="btn bg bs" onClick={() => setStatus(r.id, "hidden")}>
                {L("Ocultar", "Hide")}
              </button>
            )}
            {r.status === "hidden" && (
              <button className="btn bg bs" onClick={() => setStatus(r.id, "pending")}>
                {L("Restaurar", "Restore")}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}